import React, { useState } from 'react';
import { X, AlertTriangle, Send, Check } from 'lucide-react';
import { OrdemDeServico } from '../../types/ordemServico';
import { OcorrenciaItem } from '../../types/areaMotorista';

interface AreaMotoristaOcorrenciaModalProps {
  isOpen: boolean;
  onClose: () => void;
  os: OrdemDeServico;
  onSalvarOcorrencia: (ocorrencia: OcorrenciaItem) => Promise<boolean>;
  isSaving: boolean;
}

const TIPOS_OCORRENCIA = [
  { id: 'atraso', label: '🚦 Atraso no Trânsito / Bloqueio' },
  { id: 'cliente_ausente', label: '🏢 Cliente / Destinatário Ausente' },
  { id: 'endereco_nao_localizado', label: '📍 Endereço Incorreto / Não Localizado' },
  { id: 'recusa_cliente', label: '❌ Recusa da Mercadoria / Devolução' },
  { id: 'avaria', label: '⚠️ Avaria Identificada na Carga' },
  { id: 'veiculo_quebrado', label: '🔧 Pane Mecânica / Pneu Furado' },
  { id: 'outro', label: '📝 Outro Imprevisto' },
] as const;

export const AreaMotoristaOcorrenciaModal: React.FC<AreaMotoristaOcorrenciaModalProps> = ({
  isOpen,
  onClose,
  os,
  onSalvarOcorrencia,
  isSaving
}) => {
  const [tipo, setTipo] = useState<OcorrenciaItem['tipo']>('atraso');
  const [descricao, setDescricao] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!descricao.trim()) {
      setFormError('Por favor, detalhe o que aconteceu.');
      return;
    }

    const payload: OcorrenciaItem = {
      os_id: os.id,
      tipo,
      descricao: descricao.trim(),
      horario: new Date().toLocaleTimeString('pt-BR').substring(0, 5)
    };

    const ok = await onSalvarOcorrencia(payload);
    if (ok) {
      setSucesso(true);
      setTimeout(() => {
        setSucesso(false);
        setDescricao('');
        onClose();
      }, 1200);
    } else {
      setFormError('Erro ao registrar ocorrência. Tente novamente.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-slate-200 overflow-hidden text-left">
        
        {/* Header */}
        <div className="bg-amber-600 text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <AlertTriangle className="w-5 h-5" />
            <div>
              <h2 className="text-base font-bold">Registrar Ocorrência</h2>
              <p className="text-xs text-amber-100">OS #{os.id} • Alerta Operacional</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="text-amber-100 hover:text-white p-1 rounded-lg hover:bg-amber-700 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        {sucesso ? (
          <div className="p-8 text-center space-y-3">
            <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-7 h-7 stroke-[3]" />
            </div>
            <h3 className="text-base font-bold text-slate-900">Ocorrência Registrada!</h3>
            <p className="text-xs text-slate-600">A equipe operacional da Voope foi notificada sobre este evento.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700">
                {formError}
              </div>
            )}

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-1.5">
                Tipo da Ocorrência
              </label>
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value as any)}
                className="w-full px-3 py-2 text-sm rounded-xl border border-slate-300 focus:ring-2 focus:ring-amber-500 outline-none bg-white font-medium text-slate-800"
              >
                {TIPOS_OCORRENCIA.map(t => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-1.5">
                Descrição Detalhada do Imprevisto
              </label>
              <textarea
                required
                rows={3}
                placeholder="Explique o que ocorreu, horários estimados ou ações tomadas..."
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-xl border border-slate-300 focus:ring-2 focus:ring-amber-500 outline-none resize-none"
              />
            </div>

            <div className="pt-2 flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="flex-1 py-2.5 px-4 rounded-xl border border-slate-300 text-slate-700 font-bold text-sm hover:bg-slate-100 transition"
              >
                Voltar
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="flex-1 py-2.5 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-amber-600/20 transition disabled:opacity-50"
              >
                {isSaving ? (
                  <span>Salvando...</span>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Enviar Alerta</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
